/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneJobTab.tsx
 * FILE_ID: JOBHUB-QA-PHONE-JOBTAB-001
 * PURPOSE: Phone job QA is one page: Set Up, or the checklist.
 * WHY: The short Prepare QA sheet left the checklist unusable.
 */
import { useEffect, useState } from 'react'
import { normalizeList } from '../../../qa/qaApiHelpers'
import type { QaAreaExecution } from '../../../qa/qaApi'
import { JobQaInspectionSection } from '..'
import { JobQaSetupSection } from '..'
import { JobQaWorkspaceSection } from '..'
import type { JobQaTabController } from '../useJobQaTabController'
import { QaPhoneSheetHost } from './QaPhoneSheetHost'
import { requestQaPhoneInspection } from '../../../qa/phonePage'
import { initialQaPhonePage, qaPhoneVisiblePage, requestQaPhonePage, type QaPhonePage } from './qaPhonePage'
import { useQaPhoneUi } from './useQaPhoneUi'

export function QaPhoneJobTab({ controller }: { controller: JobQaTabController }) {
  const ui = useQaPhoneUi()
  const workspace = controller.workspace
  const [chosen, setChosen] = useState<QaPhonePage | null>(null)
  useEffect(() => {
    const onPage = (event: Event) => {
      const detail = (event as CustomEvent<{ page?: QaPhonePage; afterCreate?: boolean }>).detail
      if (detail?.page !== 'setup' && detail?.page !== 'qa') return
      if (detail.page === 'qa' && detail.afterCreate) workspace.setAreaMode('run')
      if (detail.page === 'setup') ui.closeSheet()
      setChosen(detail.page)
    }
    window.addEventListener('qa-phone-page', onPage)
    return () => window.removeEventListener('qa-phone-page', onPage)
  }, [ui, workspace])
  const inspectionsQuery = workspace.inspectionsQuery
  const areasQuery = workspace.areasQuery
  const auto = initialQaPhonePage({
    inspectionsSettled: Boolean(inspectionsQuery.isSuccess || inspectionsQuery.isError),
    inspectionCount: workspace.inspections.length,
    areasFetching: Boolean(areasQuery.isFetching),
    areasSettled: Boolean(areasQuery.isSuccess || areasQuery.isError),
    areaCount: normalizeList<QaAreaExecution>(areasQuery.data).length,
  })
  const page = qaPhoneVisiblePage(Boolean(controller.props.openSetup), chosen, auto)
  useEffect(() => {
    if (page !== 'setup') return
    const onOpen = (event: Event) => {
      if ((event as CustomEvent<{ id?: string }>).detail?.id !== 'select-qa') return
      ui.setOpenSheet('select-qa')
    }
    // Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneWorkspace.tsx (qa-phone-open-sheet)
    window.addEventListener('qa-phone-open-sheet', onOpen)
    return () => window.removeEventListener('qa-phone-open-sheet', onOpen)
  }, [page, ui])
  if (page === null) {
    return (
      <div data-testid="job-qa-tab" className="flex h-full min-h-0 flex-col gap-1 max-md:px-0">
        {/* Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/QaPhoneWorkspace.tsx (qa-phone-checklist-skeleton) */}
        <div data-testid="qa-phone-checklist-skeleton" className="mx-[var(--qa-phone-inset,0px)] grid gap-2 p-3" aria-hidden>
          <div className="h-16 rounded-lg bg-muted" />
          <div className="h-16 rounded-lg bg-muted" />
          <div className="h-16 rounded-lg bg-muted" />
        </div>
      </div>
    )
  }
  if (page === 'qa') {
    return (
      <div data-testid="job-qa-tab" className="flex h-full min-h-0 flex-col gap-1 max-md:px-0">
        <div className="min-h-0 flex-1">
          <JobQaWorkspaceSection controller={controller} />
        </div>
      </div>
    )
  }
  return (
    <div data-testid="job-qa-tab" className="flex h-full min-h-0 flex-col gap-1 max-md:px-0">
      <div data-testid="qa-phone-setup-page" className="mx-[var(--qa-phone-inset,0px)] flex h-full min-h-0 flex-col">
        <div className="shrink-0 sticky top-0 z-20 bg-background">
          <button
            type="button"
            aria-label="QA"
            onClick={() => {
              // Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/qaPhonePage.ts (requestQaPhonePage)
              requestQaPhonePage('qa')
            }}
            className="flex min-h-[44px] w-full items-center gap-2 rounded-lg border border-border bg-card px-3 text-left text-sm font-medium text-foreground"
          >
            QA
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <JobQaSetupSection
            controller={controller}
            page
            onOpenTemplates={() => ui.setOpenSheet('templates')}
            onCloseTemplates={() => ui.closeSheet()}
          />
          <JobQaInspectionSection
            workspace={workspace}
            jobId={controller.props.jobId}
            orgId={controller.props.orgId}
            quotes={controller.props.quotes}
            showRecordHome
            // Reused from: packages/job-hub/src/jobs/qa/phonePage.ts (requestQaPhoneInspection)
            onPicked={requestQaPhoneInspection}
            onOpened={() => {
              // Reused from: packages/job-hub/src/jobs/tabs/job-qa/phone/qaPhonePage.ts (requestQaPhonePage)
              requestQaPhonePage('qa')
            }}
          />
        </div>
      </div>
      <QaPhoneSheetHost
        controller={controller}
        ui={ui}
        phonePage="setup"
        areaRows={[]}
        groups={[]}
        areaIds={[]}
        items={[]}
        visibleIds={[]}
        scope="areas"
        notes={[]}
        onRefresh={() => ui.closeSheet()}
        onExport={() => ui.closeSheet()}
      />
    </div>
  )
}
